// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";

// Server derives these during sync and does not return the row, so the
// connected-state hooks stay stale until the next board load.
const CONNECTED_FLAG_BY_SECRET: Record<string, string> = {
  [PERSISTENCE_STATE_KEYS.companyCamApiKey]: PERSISTENCE_STATE_KEYS.hasCompanyCamApiKey,
  [PERSISTENCE_STATE_KEYS.openAiApiKey]: PERSISTENCE_STATE_KEYS.hasOpenAiApiKey,
};

const ALLOWED_SECRET_KEYS = new Set(Object.keys(CONNECTED_FLAG_BY_SECRET));

type SecretRecord = {
  id?: string | number;
  key: string;
  value: string;
  isPhantom?: boolean;
  meta?: { modified?: Record<string, unknown> };
  remove: () => void;
  clearChanges?: () => void;
};

type SecretStore = {
  find: (fn: (record: SecretRecord) => boolean) => SecretRecord | undefined;
  getById?: (id: string | number) => SecretRecord | undefined;
  includes?: (record: SecretRecord) => boolean;
  add?: (record: SecretDraft) => unknown;
  removed?: {
    includes: (record: SecretRecord) => boolean;
    remove: (id: string | number) => void;
  };
  added?: {
    includes: (record: SecretRecord) => boolean;
    remove: (record: SecretRecord) => void;
  };
};

type SyncProject = {
  sync: () => Promise<unknown>;
  boardUsersStore?: {
    swivuCurrentUser?: { id?: number };
  };
};

type SecretDraft = {
  key: string;
  value: string;
  user?: number;
};

export type BoardSecretOptions = {
  isSandboxBoard?: boolean;
};

function secretStore(): SecretStore {
  // Assigned in bryntumContext after the project loads; the JS binding has no declared type.
  // @ts-expect-error userPreferences is an untyped JS let
  return userPreferences.boardPreferencesStore;
}

function findSecret(key: string): SecretRecord | undefined {
  return secretStore().find((record) => record.key === key);
}

function assertAllowedSecretKey(key: string) {
  if (!ALLOWED_SECRET_KEYS.has(key)) {
    throw new Error("Unsupported board secret key");
  }
}

function assertCompanyCamAllowed(key: string, isSandboxBoard?: boolean) {
  if (key === PERSISTENCE_STATE_KEYS.companyCamApiKey && isSandboxBoard) {
    throw new Error("Sandbox boards cannot manage CompanyCam API keys");
  }
}

function clearValueModification(record: SecretRecord) {
  if (record.meta?.modified) {
    delete record.meta.modified.value;
  }
  record.clearChanges?.();
}

function restoreSecretWrite(key: string, wasPersisted: boolean, committedValue: string) {
  const record = findSecret(key);
  if (!record) return;

  if (!wasPersisted) {
    record.remove();
    return;
  }

  record.value = committedValue;
  clearValueModification(record);
}

function cancelPendingRemove(record: SecretRecord) {
  const store = secretStore();
  if (record.id != null && store.removed?.includes(record)) {
    store.removed.remove(record.id);
  }

  const stillThere = record.id != null ? store.getById?.(record.id) : undefined;
  if (!stillThere) {
    store.add?.(record);
    if (store.added?.includes(record)) {
      store.added.remove(record);
    }
  }

  clearValueModification(record);
}

function writeSecretValue(project: SyncProject, key: string, value: string) {
  const existing = findSecret(key);
  if (existing) {
    existing.value = value;
    return;
  }

  secretStore().add?.({
    key,
    value,
    user: project.boardUsersStore?.swivuCurrentUser?.id,
  });
}

function maskStoredSecret(key: string) {
  const record = findSecret(key);
  if (!record || record.value === "") return;
  record.value = "";
  clearValueModification(record);
}

function untrackLocalFlag(record: SecretRecord) {
  const store = secretStore();
  if (store.added?.includes(record)) {
    store.added.remove(record);
  }
  clearValueModification(record);
}

function mirrorConnectedFlag(project: SyncProject, secretKey: string, connected: boolean) {
  const flagKey = CONNECTED_FLAG_BY_SECRET[secretKey];
  if (!flagKey) return;

  const store = secretStore();
  const next = connected ? "true" : "false";
  let record = store.find((item) => item.key === flagKey);

  if (!record) {
    if (!connected) return;
    store.add?.({
      key: flagKey,
      value: next,
      user: project.boardUsersStore?.swivuCurrentUser?.id,
    });
    record = store.find((item) => item.key === flagKey);
    if (!record) return;
    untrackLocalFlag(record);
    return;
  }

  if (record.value === next) return;
  record.value = next;
  untrackLocalFlag(record);
}

export async function saveBoardSecret(
  project: SyncProject,
  key: string,
  value: string,
  options: BoardSecretOptions = {},
) {
  assertAllowedSecretKey(key);
  assertCompanyCamAllowed(key, options.isSandboxBoard);

  const existing = findSecret(key);
  const wasPersisted = Boolean(existing && !existing.isPhantom);
  const committedValue = existing?.value ?? "";

  writeSecretValue(project, key, value);

  try {
    await project.sync();
    maskStoredSecret(key);
    mirrorConnectedFlag(project, key, true);
  } catch (error) {
    restoreSecretWrite(key, wasPersisted, committedValue);
    throw error;
  }
}

export async function removeBoardSecret(
  project: SyncProject,
  key: string,
  options: BoardSecretOptions = {},
) {
  assertAllowedSecretKey(key);
  assertCompanyCamAllowed(key, options.isSandboxBoard);

  const existing = findSecret(key);
  // Fail closed: has-* may still be true after reload while the secret row is
  // absent or phantom-only. Never toast success unless a persisted row syncs away.
  if (!existing || existing.isPhantom) {
    throw new Error("No API key to remove");
  }

  existing.remove();

  try {
    await project.sync();
    mirrorConnectedFlag(project, key, false);
  } catch (error) {
    cancelPendingRemove(existing);
    throw error;
  }
}
